import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { BillingPageState } from "@/components/pages/billing-settings/components/BillingPageState";
import {
  useCurationDraft,
  useHotelCuration,
  useUpdateHotelCurationOverrides,
} from "@/components/pages/guesteria-partners/hooks/hotel-partner-curation.hooks";
import {
  GuesteriaActivitiesTable,
  type GuesteriaActivityRow,
} from "@/components/pages/guesteria-partners/components/GuesteriaActivitiesTable";
import { GuesteriaAddActivityFromViatorDialog } from "@/components/pages/guesteria-partners/components/GuesteriaAddActivityFromViatorDialog";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { hasFrontdeskBlockPermission } from "@/utils/accessControl";

export interface GuesteriaActivitiesForGuestsTabProps {
  hotelId: string | undefined;
  cityName?: string;
}

const GuesteriaActivitiesForGuestsContent: React.FC<
  GuesteriaActivitiesForGuestsTabProps
> = ({ hotelId, cityName }) => {
  const { t } = useTranslation();
  const { userProfile } = useAuthenticatedUser();
  const canEdit = hasFrontdeskBlockPermission(
    userProfile,
    "frontdesk_management",
    "administrate",
  );
  const [addDialogOpen, setAddDialogOpen] = useState(false);

  const curationQuery = useHotelCuration(hotelId);
  const draft = useCurationDraft(curationQuery.data);
  const updateMutation = useUpdateHotelCurationOverrides(hotelId);

  if (curationQuery.isLoading) {
    return <BillingPageState text={t("partners.curation.loading")} />;
  }

  if (curationQuery.isError || !curationQuery.data) {
    return (
      <div className="flex flex-col items-start gap-2">
        <BillingPageState
          text={t("partners.curation.loadError")}
          tone="error"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => curationQuery.refetch()}
        >
          {t("partners.curation.retry")}
        </Button>
      </div>
    );
  }

  const curation = curationQuery.data;
  const rows: GuesteriaActivityRow[] = [
    ...draft.effectiveItems.map((item) => ({
      productCode: item.productCode,
      title: item.title,
      position: item.position,
      hidden: item.hidden,
      price: item.price,
      currency: item.currency,
      imageUrl: item.imageUrl,
      source: item.source,
      tags: item.tags,
      addedByHotel: false,
    })),
    ...draft.effectiveAdded.map((item) => ({
      productCode: item.productCode,
      title: item.title,
      position: item.position,
      hidden: false,
      price: item.price,
      currency: item.currency,
      imageUrl: item.imageUrl,
      source: item.source,
      tags: item.tags,
      addedByHotel: true,
    })),
  ];
  // No "not loaded" branch to disable Save for here: the guards above
  // already swap the whole tab for a loading/error PageState before Save
  // ever renders, so `partners.curation.blockedLoading` has no reachable
  // case to attach to (see report).
  const saveDisabled =
    !canEdit || !draft.hasChanges || updateMutation.isPending;

  const handleSave = () => {
    updateMutation.mutate(draft.buildRequest(), {
      onSuccess: () => {
        toast.success(t("partners.curation.saveSuccess"));
        draft.reset();
      },
    });
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-base font-semibold text-[#171717]">
          {t("partners.curation.title")}
        </h2>
        <p className="text-muted-foreground text-sm">
          {t("partners.curation.description")}
        </p>
      </div>

      <div className="sticky top-0 z-10 flex items-center justify-end gap-3 border-b border-[#E5E5E5] bg-white py-2">
        <div className="flex items-center gap-3">
          {draft.hasChanges && (
            <span className="text-muted-foreground text-xs">
              {t("partners.curation.unsavedChanges")}
            </span>
          )}
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canEdit}
            onClick={() => setAddDialogOpen(true)}
          >
            {t("partners.curation.addFromViator")}
          </Button>
          <Button
            type="button"
            disabled={saveDisabled}
            onClick={handleSave}
            className="h-8 rounded-md bg-[#6D82A3] px-4 text-sm leading-normal font-medium text-[#FAFAFA] hover:bg-[#5F7597] disabled:opacity-50"
          >
            {updateMutation.isPending
              ? t("partners.curation.saving")
              : t("partners.curation.save")}
          </Button>
        </div>
      </div>

      {!curation.cityCurated && (
        <BillingPageState text={t("partners.curation.noCityList")} />
      )}

      <GuesteriaActivitiesTable
        rows={rows}
        canEdit={canEdit}
        onToggleHidden={draft.toggleHidden}
        onRemove={draft.stageRemove}
      />

      <GuesteriaAddActivityFromViatorDialog
        open={addDialogOpen}
        onOpenChange={setAddDialogOpen}
        hotelId={hotelId}
        cityName={cityName}
        alreadyAddedCodes={draft.addedOrCityCodes}
        onAdd={draft.stageAdd}
      />
    </div>
  );
};

/** Keyed by hotel: switching hotel drops the draft, search and sorting of the previous one. */
export const GuesteriaActivitiesForGuestsTab: React.FC<
  GuesteriaActivitiesForGuestsTabProps
> = (props) => (
  <GuesteriaActivitiesForGuestsContent key={props.hotelId} {...props} />
);
